/* eslint-disable max-lines */
// internal-deps
import { AnalyticsContextValue } from "@swiggy-private/checkout-core";

// constants
import {
    SCREEN_NAME,
    SOURCE,
    IMPRESSION_ACTIONS,
    CLICK_ACTIONS,
    IMPRESSION_LABELS,
    CLICK_LABELS,
    ACTIONS,
} from "@dineout-cart-client/analytics/constants";

// interfaces
import {
    IImpressionOnePurchase,
    IImpressionBillDetails,
    IImpressionTicketDetails,
    IClickTicketItem,
    IClickDeleteTicketItem,
    IImpressionEventBillDetails,
    IClickEventProceed,
    IImpressionEventProceed,
    IScreenViewBookingReview,
    IClickTermsAndConditions,
    IClickTor,
    IImpressionDisclaimerBanner,
    IImpressionBenefitCallout,
    IImpressionProceedCtaForTableBooking,
    IErrorHalfCardEvent,
    IErrorPageImpressionEvent,
    IImpressionCashBackEarned,
    IImpressionCashBackToggle,
    IClickCashBackToggle,
    IImpressionCashBackBenefitCallout,
    ISpecialRequestAnalytics,
} from "@dineout-cart-client/interfaces/AnalyticsEvents";

export interface ITableBookingCartPage {
    restaurantId: string;
    selectedGuestCount: number;
    selectedDate: string;
    selectedSlot: string;
    selectedOfferId: string;
    selectedOfferText: string;
    selectedSlotId: number;
    selectedDealCategory: string;
    billDetails: Record<string, string>;
    paymentMethod?: string;
    buttonVariant?: string;
    offerId?: string;
    dealCategory?: string;
    paymentType?: string;
}

export const impressionTableBookingCartPage = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    {
        restaurantId,
        selectedGuestCount,
        selectedDate,
        selectedSlot,
        selectedOfferId,
        selectedOfferText,
        selectedSlotId,
        selectedDealCategory,
        billDetails,
    }: ITableBookingCartPage,
): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: IMPRESSION_ACTIONS.CONFIRM_TABLE_BOOKING,
        context: JSON.stringify({
            restaurantId,
            selectedGuestCount,
            selectedDate,
            selectedSlot,
            selectedOfferId,
            selectedOfferText,
            selectedSlotId,
            selectedDealCategory,
            billDetails,
        }),
    });
};

export const impressionOnePurchase = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { onePlan, duration, price }: IImpressionOnePurchase,
): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: IMPRESSION_ACTIONS.ONE_PURCHASE,
        context: JSON.stringify({
            OnePlan: onePlan,
            Duration: duration,
            Price: price,
        }),
    });
};

export const impressionCashBackBenefitCallout = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { bannerText, dealCategory, restaurantId }: IImpressionCashBackBenefitCallout,
): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: IMPRESSION_ACTIONS.IMPRESSION_CASH_BACK_BENEFIT_CALLOUT,
        label: IMPRESSION_LABELS.CASHBACK_BENEFIT_CALLOUT,
        context: JSON.stringify({
            bannerText,
            dealCategory,
            restaurantId,
        }),
    });
};

export const impressionSpecialRequest = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    {
        restaurantId,
        selectedGuestCount,
        selectedDate,
        selectedSlot,
        selectedOfferText,
        selectedDealCategory,
        billDetails,
    }: ISpecialRequestAnalytics,
): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: IMPRESSION_LABELS.SPECIAL_REQUEST,
        context: JSON.stringify({
            restaurantId,
            selectedGuestCount,
            selectedDate,
            selectedSlot,
            selectedOfferText,
            selectedDealCategory,
            billDetails,
        }),
    });
};

export const clickSpecialRequest = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    {
        restaurantId,
        selectedGuestCount,
        selectedDate,
        selectedSlot,
        selectedOfferText,
        selectedDealCategory,
        billDetails,
    }: ISpecialRequestAnalytics,
): void => {
    clickEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: CLICK_LABELS.SPECIAL_REQUEST_CLICK,
        context: JSON.stringify({
            restaurantId,
            selectedGuestCount,
            selectedDate,
            selectedSlot,
            selectedOfferText,
            selectedDealCategory,
            billDetails,
        }),
    });
};

export const impressionSpecialRequestHalfcard = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    {
        restaurantId,
        selectedGuestCount,
        selectedDate,
        selectedSlot,
        selectedOfferText,
        selectedDealCategory,
        billDetails,
    }: ISpecialRequestAnalytics,
): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: IMPRESSION_LABELS.SPECIAL_REQUEST_HALFCARD,
        context: JSON.stringify({
            restaurantId,
            selectedGuestCount,
            selectedDate,
            selectedSlot,
            selectedOfferText,
            selectedDealCategory,
            billDetails,
        }),
    });
};

export const clickSubmitSpecialRequest = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    {
        restaurantId,
        selectedGuestCount,
        selectedDate,
        selectedSlot,
        selectedOfferText,
        selectedDealCategory,
        billDetails,
        instruction,
    }: ISpecialRequestAnalytics,
): void => {
    clickEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: CLICK_LABELS.SPECIAL_REQUEST_SUBMIT,
        context: JSON.stringify({
            restaurantId,
            selectedGuestCount,
            selectedDate,
            selectedSlot,
            selectedOfferText,
            selectedDealCategory,
            billDetails,
            instruction,
        }),
    });
};

export const clickDeleteSpecialRequest = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    {
        restaurantId,
        selectedGuestCount,
        selectedDate,
        selectedSlot,
        selectedOfferText,
        selectedDealCategory,
        billDetails,
    }: ISpecialRequestAnalytics,
): void => {
    clickEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: CLICK_LABELS.SPECIAL_REQUEST_DELETE,
        context: JSON.stringify({
            restaurantId,
            selectedGuestCount,
            selectedDate,
            selectedSlot,
            selectedOfferText,
            selectedDealCategory,
            billDetails,
        }),
    });
};

export const clickConfirmBookingTable = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    {
        restaurantId,
        selectedGuestCount,
        selectedDate,
        selectedSlot,
        selectedOfferId,
        selectedOfferText,
        selectedSlotId,
        selectedDealCategory,
        billDetails,
    }: ITableBookingCartPage,
): void => {
    clickEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: CLICK_ACTIONS.CONFIRM_TABLE_BOOKING,
        label: CLICK_LABELS.CONFIRM_TABLE_BOOKING_CLICK,
        context: JSON.stringify({
            restaurantId,
            offerId: selectedOfferId,
            dealCategory: selectedDealCategory,
            guests: selectedGuestCount,
            slotDisplayTime: selectedSlot,
            billDetails,
            selectedDate,
            selectedOfferText,
            selectedSlotId,
        }),
    });
};

export const impressionTurboPayButtonReservation = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    {
        restaurantId,
        selectedGuestCount,
        selectedDate,
        selectedSlot,
        selectedOfferId,
        selectedOfferText,
        selectedSlotId,
        selectedDealCategory,
        billDetails,
        paymentMethod,
        buttonVariant,
        paymentType,
    }: ITableBookingCartPage,
): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: IMPRESSION_ACTIONS.IMPRESSION_TURBO_CHECKOUT,
        label: IMPRESSION_LABELS.CONFIRM_VIEW,
        context: JSON.stringify({
            restaurantId,
            guests: selectedGuestCount,
            selectedDate,
            selectedSlot,
            selectedOfferId,
            selectedOfferText,
            selectedSlotId,
            selectedDealCategory,
            billDetails,
            paymentMethod,
            buttonVariant,
            paymentType,
        }),
    });
};

export const clickTurboPayButtonReservation = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    {
        restaurantId,
        selectedGuestCount,
        selectedDate,
        selectedSlot,
        selectedOfferId,
        selectedOfferText,
        selectedSlotId,
        selectedDealCategory,
        billDetails,
        paymentMethod,
        buttonVariant,
        paymentType,
    }: ITableBookingCartPage,
): void => {
    clickEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: CLICK_ACTIONS.CLICK_TURBO_CHECKOUT,
        label: CLICK_LABELS.CONFIRM_CLICK,
        context: JSON.stringify({
            restaurantId,
            offerId: selectedOfferId,
            dealCategory: selectedDealCategory,
            guests: selectedGuestCount,
            slotDisplayTime: selectedSlot,
            billDetails,
            selectedDate,
            selectedOfferText,
            selectedSlotId,
            paymentMethod,
            buttonVariant,
            paymentType,
        }),
    });
};

export const clickChangePaymentMethod = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    {
        restaurantId,
        selectedGuestCount,
        selectedDate,
        selectedSlot,
        selectedOfferId,
        selectedOfferText,
        selectedSlotId,
        selectedDealCategory,
        billDetails,
        paymentMethod,
        buttonVariant,
        paymentType,
    }: ITableBookingCartPage,
): void => {
    clickEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: CLICK_ACTIONS.CLICK_TURBO_PAYMENT_CHANGE,
        label: "",
        context: JSON.stringify({
            restaurantId,
            guests: selectedGuestCount,
            selectedDate,
            selectedSlot,
            selectedOfferId,
            selectedOfferText,
            selectedSlotId,
            dealCategory: selectedDealCategory,
            billDetails,
            paymentMethod,
            buttonVariant,
            paymentType,
        }),
    });
};

export const clickCashBackToggle = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    {
        eventId,
        dealCategory,
        restaurantId,
        billAmount,
        cashbackSpent,
        cashbackEarned,
        toggleState,
        remainingBalance,
        cashbackPerc,
        couponApplied,
        couponDiscount,
        couponDisabled,
        isRestaurantHaving2xDineCash,
    }: IClickCashBackToggle,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT_CART,
        action: CLICK_ACTIONS.CASH_BACK_TOGGLE,
        context: JSON.stringify({
            eventId,
            dealCategory,
            restaurantId,
            billAmount,
            cashbackSpent,
            cashbackEarned,
            toggleState,
            remainingBalance,
            cashbackPerc,
            couponApplied,
            couponDiscount,
            couponDisabled,
            isRestaurantHaving2xDineCash,
        }),
    });
};

export const impressionBillDetails = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    {
        restaurantID,
        offerId,
        dealCategory,
        guests,
        slotDetails,
        billDetails,
        paybillSource,
        paymentFlow,
    }: IImpressionBillDetails,
): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: IMPRESSION_ACTIONS.BILL_DETAILS,
        context: JSON.stringify({
            restaurantID,
            offerId,
            dealCategory,
            guests,
            slotDetails,
            billDetails,
            paybillSource,
            paymentFlow,
            source: SOURCE,
        }),
    });
};

export const clickViewOneBenefits = (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: CLICK_ACTIONS.VIEW_ONE_BENEFITS,
    });
};

export const impressionTicketDetails = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { restaurantId, eventId, tickets }: IImpressionTicketDetails,
): void => {
    impressionEvent({
        category: SCREEN_NAME.EVENT_TICKET_BOOKING,
        action: IMPRESSION_ACTIONS.BOOKING_DETAILS,
        label: IMPRESSION_LABELS.GUEST_DETAILS_IMPRESSION,
        context: JSON.stringify({
            restaurantId,
            eventId,
            tickets,
        }),
    });
};

export const clickTicketItem = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { restaurantId, eventId, ticketId, numberOfGuests, amount }: IClickTicketItem,
): void => {
    clickEvent({
        category: SCREEN_NAME.EVENT_TICKET_BOOKING,
        action: CLICK_ACTIONS.BOOKING_DETAILS,
        label: CLICK_LABELS.GUEST_DETAILS_CLICK,
        context: JSON.stringify({
            restaurantId,
            eventId,
            ticketId,
            numberOfGuests,
            amount,
        }),
    });
};

export const clickDeleteTicketItem = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { restaurantId, eventId, ticketId, numberOfGuests, amount }: IClickDeleteTicketItem,
): void => {
    clickEvent({
        category: SCREEN_NAME.EVENT_TICKET_BOOKING,
        action: CLICK_ACTIONS.BOOKING_DETAILS,
        label: CLICK_LABELS.DELETE_GUEST,
        context: JSON.stringify({
            restaurantId,
            eventId,
            ticketId,
            numberOfGuests,
            amount,
        }),
    });
};

export const impressionEventBillDetails = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { restaurantID, eventId, guests, billDetails, ticketDetails }: IImpressionEventBillDetails,
): void => {
    impressionEvent({
        category: SCREEN_NAME.EVENT_TICKET_BOOKING,
        action: IMPRESSION_ACTIONS.BUY_DEAL,
        label: IMPRESSION_LABELS.DEAL_CONFIRMED_VIEW,
        context: JSON.stringify({
            restaurantID,
            eventId,
            guests,
            billDetails,
            ticketDetails,
        }),
    });
};

export const clickProceedCtaForEventBooking = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { restaurantId, eventId, ticketDetails, dealCategory, guests, billDetails, buttonStatus }: IClickEventProceed,
): void => {
    clickEvent({
        category: SCREEN_NAME.EVENT_TICKET_BOOKING,
        action: CLICK_ACTIONS.BOOKING_DETAILS,
        label: CLICK_LABELS.PAY_CLICK,
        context: JSON.stringify({
            restaurantId,
            eventId,
            ticketDetails,
            dealCategory,
            guests,
            billDetails,
            buttonStatus,
        }),
    });
};

export const impressionProceedCtaForEventBooking = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { restaurantId, eventId, ticketDetails, dealCategory, guests, billDetails, buttonStatus }: IImpressionEventProceed,
): void => {
    impressionEvent({
        category: SCREEN_NAME.EVENT_TICKET_BOOKING,
        action: IMPRESSION_ACTIONS.BOOKING_DETAILS,
        label: IMPRESSION_LABELS.PAY_IMPRESSION,
        context: JSON.stringify({
            restaurantId,
            eventId,
            ticketDetails,
            dealCategory,
            guests,
            billDetails,
            buttonStatus,
        }),
    });
};

export const impressionProceedCtaForTableBooking = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    {
        restaurantId,
        guests,
        selectedDate,
        selectedSlot,
        selectedOfferId,
        selectedOfferText,
        selectedSlotId,
        dealCategory,
        billDetails,
    }: IImpressionProceedCtaForTableBooking,
): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: IMPRESSION_ACTIONS.CONFIRM_TABLE_BOOKING,
        context: JSON.stringify({
            restaurantId,
            guests,
            selectedDate,
            selectedSlot,
            selectedOfferId,
            selectedOfferText,
            selectedSlotId,
            dealCategory,
            billDetails,
        }),
    });
};

// Generic error half-card analytics (ARD: error events). Intentionally kept
// screen-agnostic (no category) so any error half-card can reuse it.
export const impressionErrorHalfCard = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    {
        statusCode,
        statusTitle,
        statusMessage,
        ctaText,
        secondaryCtaText,
        ctaAction,
        secondaryCtaAction,
    }: IErrorHalfCardEvent,
): void => {
    impressionEvent({
        action: IMPRESSION_ACTIONS.ERROR_HALF_CARD,
        context: JSON.stringify({
            statusCode,
            statusTitle,
            statusMessage,
            ctaText,
            secondaryCtaText,
            ctaAction,
            secondaryCtaAction,
        }),
    });
};

export const clickErrorHalfCard = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    {
        statusCode,
        statusTitle,
        statusMessage,
        ctaText,
        secondaryCtaText,
        ctaAction,
        secondaryCtaAction,
        clickedCtaType,
    }: IErrorHalfCardEvent,
): void => {
    clickEvent({
        action: CLICK_ACTIONS.ERROR_HALF_CARD,
        label: ctaText,
        context: JSON.stringify({
            statusCode,
            statusTitle,
            statusMessage,
            ctaText,
            secondaryCtaText,
            ctaAction,
            secondaryCtaAction,
            clickedCtaType,
        }),
    });
};

export const impressionCashBackEarned = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    {
        eventId,
        dealCategory,
        restaurantId,
        billAmount,
        cashbackToBeEarned,
        cashbackPerc,
        isRestaurantHaving2xDineCash,
    }: IImpressionCashBackEarned,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT_CART,
        action: IMPRESSION_ACTIONS.CASH_BACK_EARNED,
        context: JSON.stringify({
            eventId,
            dealCategory,
            restaurantId,
            billAmount,
            cashbackToBeEarned,
            cashbackPerc,
            isRestaurantHaving2xDineCash,
        }),
    });
};

export const impressionCashBackToggle = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    {
        eventId,
        dealCategory,
        restaurantId,
        billAmount,
        cashbackToSpend,
        cashbackToEarn,
        toggleState,
        remainingBalance,
        cashbackPerc,
        couponApplied,
        couponDiscount,
        isRestaurantHaving2xDineCash,
    }: IImpressionCashBackToggle,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT_CART,
        action: IMPRESSION_ACTIONS.CASH_BACK_TOGGLE,
        context: JSON.stringify({
            eventId,
            dealCategory,
            restaurantId,
            billAmount,
            cashbackToSpend,
            cashbackToEarn,
            toggleState,
            remainingBalance,
            cashbackPerc,
            couponApplied,
            couponDiscount,
            isRestaurantHaving2xDineCash,
        }),
    });
};

export const screenViewBookingReview = (
    screenViewEvent: AnalyticsContextValue["screenViewEvent"],
    { restaurantId, eventId, ticketId }: IScreenViewBookingReview,
): void => {
    screenViewEvent({
        category: SCREEN_NAME.EVENT_TICKET_BOOKING,
        action: SCREEN_NAME.EVENT_TICKET_BOOKING,
        context: JSON.stringify({
            restaurantId,
            eventId,
            ticketId,
        }),
    });
};

export const clickTermsAndConditions = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { restaurantId, guests, dealCategory }: IClickTermsAndConditions,
): void => {
    clickEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: CLICK_ACTIONS.TERMS_AND_CONDITIONS,
        label: CLICK_LABELS.TERMS_AND_CONDITIONS_CLICK,
        context: JSON.stringify({
            restaurantId,
            guests,
            dealCategory,
        }),
    });
};

export const clickTermsAndConditionsForEventBooking = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { restaurantId, guests, dealCategory }: IClickTermsAndConditions,
): void => {
    clickEvent({
        category: SCREEN_NAME.EVENT_TICKET_BOOKING,
        action: CLICK_ACTIONS.TERMS_AND_CONDITIONS,
        label: CLICK_LABELS.TERMS_AND_CONDITIONS_CLICK,
        context: JSON.stringify({
            restaurantId,
            guests,
            dealCategory,
        }),
    });
};

export const clickTor = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { restaurantId, guests, dealCategory }: IClickTor,
) => {
    clickEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: CLICK_ACTIONS.CLICK_TOR,
        label: CLICK_LABELS.TOR_CLICK,
        context: JSON.stringify({
            restaurantId,
            guests,
            dealCategory,
        }),
    });
};

export const impressionDisclaimerBanner = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { restaurantId, billAmount, source, dealId }: IImpressionDisclaimerBanner,
): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: IMPRESSION_ACTIONS.IMPRESSION_DISCLAIMER_BANNER,
        context: JSON.stringify({
            restaurantId,
            billAmount,
            source,
            dealId,
        }),
    });
};

export const impressionBenefitCallout = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { offerText }: IImpressionBenefitCallout,
): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKING_REVIEW_PAGE,
        action: IMPRESSION_ACTIONS.IMPRESSION_BENEFIT_CALLOUT,
        label: IMPRESSION_LABELS.BOOKING_CARD,
        context: JSON.stringify({
            offerText,
        }),
    });
};

export const scanQrErrorPageImpressionEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { restaurantId, paymentFlow, paybillSource }: IErrorPageImpressionEvent,
) => {
    impressionEvent({
        category: SCREEN_NAME.DO_SCAN_SCREEN,
        action: IMPRESSION_ACTIONS.SCAN_QR_ERROR_PAGE,
        label: IMPRESSION_LABELS.SCAN_AGAIN_IMPRESSION,
        context: JSON.stringify({
            restaurantId,
            paymentFlow,
            paybillSource,
        }),
    });
};

export const scanQrErrorPageBackClickEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { restaurantId, paymentFlow, paybillSource }: IErrorPageImpressionEvent,
) => {
    clickEvent({
        category: SCREEN_NAME.DO_SCAN_SCREEN,
        action: CLICK_ACTIONS.SCAN_QR_ERROR_PAGE,
        label: CLICK_LABELS.BACK_CLICK,
        context: JSON.stringify({
            restaurantId,
            paymentFlow,
            paybillSource,
        }),
    });
};

export const scanQrErrorPagePrimaryActionClickEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { restaurantId, paymentFlow, paybillSource }: IErrorPageImpressionEvent,
) => {
    clickEvent({
        category: SCREEN_NAME.DO_SCAN_SCREEN,
        action: CLICK_ACTIONS.SCAN_QR_ERROR_PAGE,
        label: CLICK_LABELS.SCAN_AGAIN_CLICK,
        context: JSON.stringify({
            restaurantId,
            paymentFlow,
            paybillSource,
        }),
    });
};

export const scanQRBottomPopupScanAgainClickEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { restaurantId, paymentFlow, paybillSource, qrStatus }: IErrorPageImpressionEvent,
) => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT_CART,
        action: ACTIONS.PAYMENT_CART,
        label: CLICK_LABELS.BOTTOM_POPUP_SCAN_AGAIN_CLICK,
        context: JSON.stringify({
            restaurantId,
            paymentFlow,
            paybillSource,
            qrStatus,
        }),
    });
};

export const scanQRBottomPopupScanAgainImpressionEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { restaurantId, paymentFlow, paybillSource, qrStatus }: IErrorPageImpressionEvent,
) => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT_CART,
        action: ACTIONS.PAYMENT_CART,
        label: IMPRESSION_LABELS.BOTTOM_POPUP_SCAN_AGAIN_IMPRESSION,
        context: JSON.stringify({
            restaurantId,
            paymentFlow,
            paybillSource,
            qrStatus,
        }),
    });
};
